import { Link, useRouterState } from '@tanstack/react-router';
import { Dumbbell, Home, LineChart, User, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/cn';
// Deep-import the hooks directly to avoid pulling the entire feature
// presentation barrels (CoachPage, AthletePage, CommentsCard, dialogs) into
// the main bundle. BottomNav lives in the always-loaded layout, so a barrel
// import would defeat the lazy route split. See ROADMAP "Bundle warning"
// backlog item.
import { useUnreadCommentCount } from '@features/comments/application/use-comments';

interface NavItem {
  readonly to: string;
  readonly icon: typeof Home;
  readonly key: string;
}

// Coach tab ships in the default nav for everyone — empty state on /coach
// has the "Добавить атлета" CTA, so this also doubles as the discovery entry
// point. Hiding the tab until you already have athletes was a chicken-and-egg
// onboarding hole.
const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', icon: Home, key: 'home' },
  { to: '/programs', icon: Dumbbell, key: 'programs' },
  { to: '/coach', icon: Users, key: 'coach' },
  { to: '/progress', icon: LineChart, key: 'progress' },
  { to: '/profile', icon: User, key: 'profile' },
];

export function BottomNav(): React.JSX.Element {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Unread badge on the Home tab — coaches comment on athletes' sessions and
  // the athlete sees the dot until they open the workout.
  const unreadQuery = useUnreadCommentCount();
  const unread = unreadQuery.data ?? 0;

  return (
    <nav
      aria-label={t('nav.primary')}
      className="border-border bg-background/85 sticky bottom-0 z-10 grid grid-cols-5 border-t pb-[env(safe-area-inset-bottom)] backdrop-blur-xl"
    >
      {NAV_ITEMS.map(({ to, icon: Icon, key }) => {
        const active = pathname === to || (to !== '/' && pathname.startsWith(`${to}/`));
        return (
          <Link
            key={to}
            to={to}
            className={cn(
              // The whole label clickbox stays comfortable; the visible state
              // change happens on the icon + the underline pill so the active
              // tab reads instantly even in a sunlit gym.
              'group relative flex flex-col items-center gap-1 px-2 pt-2 pb-1.5 text-[11px] transition-colors',
              active
                ? 'text-foreground'
                : 'text-muted-foreground/80 hover:text-foreground focus-visible:text-foreground',
              'focus-visible:outline-none',
            )}
            aria-current={active ? 'page' : undefined}
          >
            <span className="relative flex items-center justify-center">
              <Icon
                className={cn('size-5 transition-colors', active ? 'text-brand' : 'text-current')}
                aria-hidden="true"
              />
              {key === 'home' && unread > 0 ? (
                <span
                  className="bg-destructive text-destructive-foreground absolute -top-1 -right-2 inline-flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-medium tabular-nums"
                  aria-label={t('comments.unreadBadge', { count: unread })}
                >
                  {unread > 9 ? '9+' : unread}
                </span>
              ) : null}
            </span>
            <span className={cn('leading-none', active && 'font-medium')}>{t(`nav.${key}`)}</span>
            {/* Underline indicator — iOS/Linear style. Width matches icon row,
                fades in only on active so motion-reduce users still see the
                state instantly via icon-color. */}
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute inset-x-1/2 -bottom-px h-0.5 w-6 -translate-x-1/2 rounded-full transition-colors',
                active ? 'bg-brand' : 'bg-transparent',
              )}
            />
          </Link>
        );
      })}
    </nav>
  );
}
